import React from 'react';
import { Icon } from '@iconify/react';
import type { TeamInvitationData, TeamMemberData } from '../../../types/n8nResponses';

/**
 * TeamDirectory — the shared list style used across Team view (Members,
 * Campaigns, Sellers). One container, one toolbar, one column-header row,
 * one row grid, one empty state.
 *
 * Layout contract (mirrors .team-member-directory):
 *  - toolbar:   search slot + custom extras (filters/buttons) + result count
 *  - columns:   visible ≥1051px, aria-hidden (rows carry field labels instead)
 *  - rows:      each row renders inside .team-directory-entry; the grid
 *               template lives in CSS (team-directory-cols-{n}); on narrow
 *               screens columns collapse under the identity cell with labels
 *
 * Callers define columns declaratively; cells can be anything (text, select,
 * badge, button). Primary cell (avatar + name) is built-in via `identity`.
 */

export interface TeamDirectoryColumn<T> {
  /** Column header text. */
  label: string;
  header?: React.ReactNode;
  /** Stable key for React. */
  key: string;
  /** Cell renderer for a row. */
  render: (item: T) => React.ReactNode;
  /** Text shown as the field label on narrow screens. Defaults to label. */
  narrowLabel?: string;
  /** Hide this column's header/cell on narrow screens entirely. */
  narrowHidden?: boolean;
}

export interface TeamDirectoryIdentityCell {
  /** Avatar node (icon or MemberAvatar). */
  avatar: React.ReactNode;
  /** Primary line (name). */
  title: React.ReactNode;
  /** Secondary line (email/subtitle) — optional. */
  subtitle?: React.ReactNode;
}

export interface TeamDirectoryActionCell {
  /** Rendered in the trailing action slot (usually a button). */
  content?: React.ReactNode;
  /** When true the action slot renders nothing (e.g. protected member). */
  hide?: boolean;
}

interface TeamDirectoryProps<T> {
  groupBy?: (item: T) => string;
  groupOrder?: string[];
  identityLabel?: React.ReactNode;
  hideToolbar?: boolean;
  unframed?: boolean;
  onOpen?: (item: T) => void;
  /** aria-label for the list region. */
  listLabel: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Extra toolbar content between search and count (filters, buttons). */
  toolbarExtras?: React.ReactNode;
  /** Result count line, e.g. "3 of 7 people". Rendered only when provided. */
  resultCount?: React.ReactNode;
  /** Column definitions (without the leading identity column). */
  columns: Array<TeamDirectoryColumn<T>>;
  rows: T[];
  /** Stable React key per row. */
  rowKey: (item: T) => string;
  /** Leading identity cell per row (avatar + title + subtitle). */
  identity: (item: T) => TeamDirectoryIdentityCell;
  /** Trailing action cell per row — omit the property to hide the slot. */
  action?: (item: T) => TeamDirectoryActionCell;
  /** Grid template — matches a `team-directory-cols-*` CSS class. */
  columnsVariant?: 'wide';
  emptyState: { icon: string; title: string; description: string };
  emptyAction?: React.ReactNode;
}

function TeamDirectoryInner<T>({
  listLabel, groupBy, groupOrder, identityLabel = 'Member', hideToolbar = false, unframed = false, onOpen,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  toolbarExtras,
  resultCount,
  columns,
  rows,
  rowKey,
  identity,
  action,
  columnsVariant = 'wide',
  emptyState,
  emptyAction,
}: TeamDirectoryProps<T>) {
  const hasActionSlot = Boolean(action);
  const groups = new Map<string, T[]>();
  for (const item of rows) {
    const key = groupBy?.(item) || '';
    const items = groups.get(key) || [];
    items.push(item); groups.set(key, items);
  }
  const groupedRows = Array.from(groups, ([label, items]) => ({label, items}));
  if (groupOrder) groupedRows.sort((a,b) => groupOrder.indexOf(a.label) - groupOrder.indexOf(b.label));

  return (
    <div className={unframed ? undefined : "team-member-directory"} style={{ '--directory-columns': columns.filter(c => !c.narrowHidden).length, '--directory-all-columns': columns.length, '--directory-action-width': hasActionSlot ? '108px' : '0px' } as React.CSSProperties}>
      {!hideToolbar && <div className="team-directory-toolbar">
        <div className="team-directory-search">
          <Icon icon="bx:search" aria-hidden="true" />
          <input
            type="search"
            aria-label={`Search ${listLabel}`}
            placeholder={searchPlaceholder}
            className="team-search"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        {toolbarExtras}
        {resultCount !== undefined && (
          <span className="team-directory-count" role="status">{resultCount}</span>
        )}
      </div>}

      <div
        className={`team-member-row team-member-columns team-directory-cols-${columnsVariant} ${columns.length === 0 ? 'team-directory-columns-empty' : ''}`}
      >
        <span>{identityLabel}</span>
        {columns.map((column) => (
          <span key={column.key} className={column.narrowHidden ? 'team-directory-col-narrow-hidden' : undefined}>
            {column.header ?? column.label}
          </span>
        ))}
        {hasActionSlot && <span />}
      </div>

      {rows.length === 0 ? (
        <div className="team-directory-empty">
          <Icon icon={emptyState.icon} aria-hidden="true" />
          <h3>{emptyState.title}</h3>
          <p>{emptyState.description}</p>
          {rows.length === 0 && emptyAction}
        </div>
      ) : (
        <ul aria-label={listLabel}>
          {groupedRows.map(group => <React.Fragment key={group.label}>
            {group.label && <li className="team-directory-group"><h3>{group.label}<span>{group.items.length}</span></h3></li>}
            {group.items.map((item) => {
            const cell = identity(item);
            const actionCell = action?.(item);
            return (
              <li key={rowKey(item)} className={`team-directory-entry${onOpen ? ' team-directory-clickable' : ''}`} onClick={onOpen ? () => onOpen(item) : undefined}>
                <div className={`team-member-row team-directory-cols-${columnsVariant}`}>
                  <div className="team-member-identity">
                    {cell.avatar}
                    <div className="min-w-0">
                      <p className="team-directory-name">{cell.title}</p>
                      {cell.subtitle !== undefined && (
                        <p className="team-directory-email">{cell.subtitle}</p>
                      )}
                    </div>
                  </div>
                  {columns.map((column) => (
                    <div key={column.key} className={`team-directory-field ${column.narrowHidden ? 'team-directory-col-narrow-hidden' : ''}`}>
                      <span className="team-directory-field-label">{column.narrowLabel ?? column.label}</span>
                      {column.render(item)}
                    </div>
                  ))}
                  {hasActionSlot && (
                    <div className="team-member-action">
                      {actionCell && !actionCell.hide ? actionCell.content : null}
                    </div>
                  )}
                </div>
              </li>
            );
          })}</React.Fragment>)}
        </ul>
      )}
    </div>
  );
}

/** Generic + memo so parent re-renders stay cheap on big rosters. */
export const TeamDirectory = React.memo(TeamDirectoryInner) as typeof TeamDirectoryInner;

export type { TeamMemberData, TeamInvitationData };
